/**
 * useContextUsers — Generic hook for managing context-users (channels,
 * funnels, ticket-pipelines).
 *
 * Routes API calls based on `contextRef.kind`:
 *   channel  → /channels/:id/users
 *   funnel   → /funnels/:id/users
 *   pipeline → /ticket-pipelines/:id/users
 *
 * Provides: list, add, update, remove mutations — same shape for all 3.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

export type ContextKind = 'channel' | 'funnel' | 'pipeline' | 'shift';

export interface ContextRef {
  kind: ContextKind;
  id: string;
}

function basePath(ref: ContextRef): string {
  switch (ref.kind) {
    case 'channel':
      return `channels/${ref.id}/users`;
    case 'funnel':
      return `funnels/${ref.id}/users`;
    case 'pipeline':
      return `ticket-pipelines/${ref.id}/users`;
    case 'shift':
      return `shifts/${ref.id}/members`;
  }
}

export interface ContextUser {
  id: string;
  userId: string;
  role: 'attendant' | 'viewer' | 'manager';
  scope: 'own' | 'team' | 'all';
  priority: number | null;
  status: 'active' | 'inactive';
  assignedAt: string;
  // Populated fields (joined by backend):
  name?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  avatar?: string;
}

export interface AddContextUserInput {
  userId: string;
  role: 'attendant' | 'viewer' | 'manager';
  scope?: 'own' | 'team' | 'all';
  priority?: number | null;
}

export interface UpdateContextUserInput {
  role?: 'attendant' | 'viewer' | 'manager';
  scope?: 'own' | 'team' | 'all';
  priority?: number | null;
  status?: 'active' | 'inactive';
}

export function useContextUsers(ref: ContextRef | undefined) {
  const queryClient = useQueryClient();
  const path = ref ? basePath(ref) : '';
  const queryKey = ['context-users', ref?.kind, ref?.id];

  const listQuery = useQuery<ContextUser[]>({
    queryKey,
    queryFn: async () => {
      const response = await api.get(path);
      // Shifts retorna { members: [...] }, demais retornam { data: [...] }
      return ref?.kind === 'shift' ? response.data.members : response.data.data;
    },
    enabled: !!ref?.id,
    staleTime: 5 * 60 * 1000,
  });

  const addMutation = useMutation({
    mutationFn: async (data: AddContextUserInput) => {
      const response = await api.post(path, data);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({
      userId,
      updates,
    }: {
      userId: string;
      updates: UpdateContextUserInput;
    }) => {
      const response = await api.patch(`${path}/${userId}`, updates);
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`${path}/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  return {
    users: listQuery.data ?? [],
    isLoading: listQuery.isLoading,
    add: addMutation,
    update: updateMutation,
    remove: removeMutation,
  };
}
